import { useRegisterSW } from 'virtual:pwa-register/react';

export interface ServiceWorkerUpdate {
  /** A new build finished downloading and is waiting - `registerType: 'prompt'`
   * (`vite.config.ts`) never activates it on its own, so this stays `false` until here. */
  readonly needRefresh: boolean;
  /** Activates the waiting worker and reloads once it has taken control. Only meaningful while
   * `needRefresh` is `true`. */
  readonly applyUpdate: () => void;
}

/**
 * The owner-facing half of the PWA update flow (subphase 6.7). `virtual:pwa-register/react`'s
 * `useRegisterSW` both registers the Workbox-generated worker (a no-op in development, where
 * `vite.config.ts` sets `devOptions.enabled: false`) and tracks whether a new one is installed
 * and waiting. This hook only reshapes that tuple-based API into the two values `UpdateBanner`
 * needs, so the registration call itself stays in exactly one place.
 *
 * `useRegisterSWHook` is injected so a test can control `needRefresh` without a real service
 * worker (`virtual:pwa-register/react` resolves to a dev-mode stub under Vitest's jsdom
 * environment, which never reports a refresh).
 */
export function useServiceWorkerUpdate(
  useRegisterSWHook: typeof useRegisterSW = useRegisterSW,
): ServiceWorkerUpdate {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSWHook();

  return {
    needRefresh,
    applyUpdate: () => {
      void updateServiceWorker(true);
    },
  };
}
